import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import * as api from '../services/apiClient';
import type { AnalysisListItem } from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useAppContext } from '../context/AppContext';
import { useImpersonation } from '../context/ImpersonationContext';
import { ConfirmationModal } from './ConfirmationModal';
import '../styles/PlaybookCommon.css';
import './AnalyseDocPage.css';

// Helper component for scrolling text on overflow
const ScrollingText: React.FC<{ text: string; className?: string }> = ({ text, className }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };
    checkOverflow();
    // Re-check on window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text]);

  return (
    <div className="analyse-history-item-name-wrapper">
      <span
        ref={textRef}
        className={`${className || ''} ${isOverflowing ? 'is-overflowing' : ''}`}
      >
        {text}
      </span>
    </div>
  );
};

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

type UploadStatus = 'idle' | 'hashing' | 'uploading' | 'analyzing' | 'success' | 'error';

interface DocumentResult {
  id: number;
  filename: string;
  status: string;
  alreadyExists: boolean;
}

type ClauseStatus = 'Optimale' | 'Incertaine' | 'Critique' | 'Manquante' | 'Supplementaire';

interface AnalysisClause {
  id: number;
  document_clause_name: string;
  matched_clause_name?: string;
  matched_clause_id?: number;
  content: string;
  status: ClauseStatus;
  explanation: string;
  reformulation?: string;
}

interface AnalysisDetails {
  // Step 4 - Analyse
  contract_type_id?: number;
  contract_type_name?: string;
  company_role?: string;
  parties?: string[];
  summary?: string;
  // Step 5 - Conformité
  status?: string;
  explanation?: string;
  // Step 6 - Clauses
  expected_clauses_count?: number;
  found_clauses_count?: number;
  extra_clauses_count?: number;
  clauses?: AnalysisClause[];
  // Step 7 - Completed
  document_id?: number;
  markdown_length?: number;
  tokens_estimated?: number;
  final_note?: number;
}

interface AnalysisProgress {
  step: number;
  totalSteps: number;
  message: string;
  details?: AnalysisDetails;
}

// Define the analysis steps for the progress display (matches backend SSE steps 1-6)
const ANALYSIS_STEPS = [
  { id: 1, label: 'Préparation' },
  { id: 2, label: 'Import' },
  { id: 3, label: 'Classification' },
  { id: 4, label: 'Analyse' },
  { id: 5, label: 'Segmentation' },
  { id: 6, label: 'Audit' },
  { id: 7, label: 'Validation' },
];

// Labels for clause statuses (singular and plural)
const CLAUSE_STATUS_LABELS: Record<ClauseStatus, { singular: string; plural: string }> = {
  Optimale: { singular: 'Optimale', plural: 'Optimales' },
  Incertaine: { singular: 'Incertaine', plural: 'Incertaines' },
  Critique: { singular: 'Critique', plural: 'Critiques' },
  Manquante: { singular: 'Manquante', plural: 'Manquantes' },
  Supplementaire: { singular: 'Supplémentaire', plural: 'Supplémentaires' },
};

// Helper to get label based on count
const getStatusLabel = (status: ClauseStatus, count: number): string => {
  const labels = CLAUSE_STATUS_LABELS[status];
  if (!labels) return status;
  return count <= 1 ? labels.singular : labels.plural;
};

// All clause filter options
const CLAUSE_FILTERS: ClauseStatus[] = ['Optimale', 'Incertaine', 'Critique', 'Manquante', 'Supplementaire'];

// Valid clause status values for runtime validation
const VALID_CLAUSE_STATUSES: readonly string[] = ['Optimale', 'Incertaine', 'Critique', 'Manquante', 'Supplementaire'];

/**
 * Validate if a value is a valid ClauseStatus
 */
function isValidClauseStatus(value: unknown): value is ClauseStatus {
  return typeof value === 'string' && VALID_CLAUSE_STATUSES.includes(value);
}

/**
 * Compute SHA-256 hash of a file using native Web Crypto API
 */
async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Get content type for file
 */
function getContentType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
}

export const AnalyseDocPage: React.FC = () => {
  const { authStatus } = useAuth();
  const { setActivePage, setInitialContractId, setInitialClauseId } = useAppContext();
  const { isImpersonating, activeAlias } = useImpersonation();
  const companyName = isImpersonating && activeAlias
    ? activeAlias.company_name
    : (authStatus?.user?.company || 'Votre entreprise');

  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [documentResult, setDocumentResult] = useState<DocumentResult | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);
  const [clauseSearchQuery, setClauseSearchQuery] = useState('');
  const [activeClauseFilters, setActiveClauseFilters] = useState<ClauseStatus[]>([]);

  // History panel state
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(true);
  const [analysesList, setAnalysesList] = useState<AnalysisListItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const [showCompanyAnalyses, setShowCompanyAnalyses] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<{ id: number; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Existing analysis modal state
  const [existingAnalysisModalOpen, setExistingAnalysisModalOpen] = useState(false);
  const [pendingExistingAnalysis, setPendingExistingAnalysis] = useState<{
    documentId: number;
    filename: string;
    overallNote?: number;
  } | null>(null);

  // Check if user is admin
  const isAdmin = authStatus?.user?.is_admin === true;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // Refs for handling async operations safely
  const analysisProgressRef = useRef<AnalysisProgress | null>(null);
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const existingAnalysisModalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Keep analysisProgressRef in sync with state
  useEffect(() => {
    analysisProgressRef.current = analysisProgress;
  }, [analysisProgress]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, []);

  // Focus management for existing analysis modal
  useEffect(() => {
    if (existingAnalysisModalOpen) {
      // Save currently focused element
      previouslyFocusedRef.current = document.activeElement as HTMLElement;
      // Focus the modal
      existingAnalysisModalRef.current?.focus();
    } else {
      // Restore focus when modal closes
      previouslyFocusedRef.current?.focus();
      previouslyFocusedRef.current = null;
    }
  }, [existingAnalysisModalOpen]);

  // Clauses accumulated from SSE clause_result events
  const clausesData = analysisProgress?.details?.clauses || [];
  // Show skeleton only when analyzing and no clauses have arrived yet
  const clausesLoading = uploadStatus === 'analyzing' && clausesData.length === 0;

  // Global score from backend (final_note) with color determination
  const globalScore = useMemo(() => {
    const score = analysisProgress?.details?.final_note;

    // Determine color class based on score
    let colorClass: string;
    if (score === undefined || score === null) {
      colorClass = 'gray'; // Loading state
    } else if (score === 5) {
      colorClass = 'green';
    } else if (score >= 3) {
      colorClass = 'yellow';
    } else {
      colorClass = 'red';
    }

    return { score, colorClass };
  }, [analysisProgress?.details?.final_note]);

  // Filter clauses based on search query and active filters
  const filteredClauses = useMemo(() => {
    let clauses = clausesData;

    // Filter by search query
    if (clauseSearchQuery.trim()) {
      const query = clauseSearchQuery.toLowerCase();
      clauses = clauses.filter(c =>
        c.document_clause_name.toLowerCase().includes(query) ||
        c.matched_clause_name?.toLowerCase().includes(query) ||
        c.content.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (activeClauseFilters.length > 0) {
      clauses = clauses.filter(c => activeClauseFilters.includes(c.status));
    }

    return clauses;
  }, [clausesData, clauseSearchQuery, activeClauseFilters]);

  // Filter history analyses based on search query
  const filteredAnalyses = useMemo(() => {
    if (!historySearchQuery.trim()) {
      return analysesList;
    }
    const query = historySearchQuery.toLowerCase();
    return analysesList.filter(a =>
      a.document_name.toLowerCase().includes(query)
    );
  }, [analysesList, historySearchQuery]);

  // Fetch history analyses
  const fetchHistoryAnalyses = useCallback(async () => {
    if (!mountedRef.current) return;
    setIsLoadingHistory(true);
    try {
      const response = await api.getDocumentAnalyses({
        limit: 50,
        getAllCompanyAnalysis: isAdmin && showCompanyAnalyses
      });
      if (!mountedRef.current) return;
      if (response.success && response.data) {
        setAnalysesList(response.data.analyses);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('Failed to fetch analyses history:', error);
    } finally {
      if (mountedRef.current) {
        setIsLoadingHistory(false);
      }
    }
  }, [isAdmin, showCompanyAnalyses, activeAlias]);

  // Load analysis from history
  const loadHistoryAnalysis = useCallback(async (documentId: number) => {
    if (!mountedRef.current) return;
    setIsLoadingHistory(true);
    try {
      const response = await api.getDocumentAnalysisDetail(documentId);
      if (!mountedRef.current) return;
      if (response.success && response.data) {
        const data = response.data;

        // Map API response to existing state structure
        setAnalysisProgress({
          step: 7,
          totalSteps: 7,
          message: 'Analyse terminée',
          details: {
            contract_type_id: data.contract_type_id,
            contract_type_name: data.contract_type_name,
            company_role: data.company_role,
            parties: data.parties,
            summary: data.summary,
            status: data.directives_status,
            explanation: data.directives_explanation,
            expected_clauses_count: data.statistics.total_clauses,
            found_clauses_count: data.statistics.conform_count + data.statistics.warning_count + data.statistics.critical_count,
            extra_clauses_count: data.statistics.extra_count,
            clauses: data.clauses.map(c => ({
              id: c.id,
              document_clause_name: c.document_clause_name,
              matched_clause_name: c.matched_clause_name ?? undefined,
              matched_clause_id: c.matched_clause_id ?? undefined,
              content: c.content,
              status: (isValidClauseStatus(c.status) ? c.status : 'Incertaine') as ClauseStatus,
              explanation: c.explanation,
              reformulation: c.reformulation ?? undefined,
            })),
            final_note: data.overall_note,
          }
        });

        setDocumentResult({
          id: data.document_id,
          filename: data.document_name,
          status: 'COMPLETED',
          alreadyExists: true,
        });

        setSelectedFile(null); // No actual file when loading from history
        setUploadStatus('success');
        setIsHistoryPanelOpen(false);
        setSelectedHistoryId(documentId);
        setIsStepperCollapsed(true);
      }
    } catch (error) {
      if (!mountedRef.current) return;
      console.error('Failed to load analysis:', error);
    } finally {
      if (mountedRef.current) {
        setIsLoadingHistory(false);
      }
    }
  }, []);

  // Load history on mount
  useEffect(() => {
    fetchHistoryAnalyses();
  }, [fetchHistoryAnalyses]);

  // Reset state when impersonation changes
  useEffect(() => {
    // Abort any in-progress operations
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // Clear fallback timeout if any
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    // Reset all state
    setAnalysesList([]);
    setSelectedHistoryId(null);
    setAnalysisProgress(null);
    setDocumentResult(null);
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage(null);
    setIsStepperCollapsed(false);
    setClauseSearchQuery('');
    setActiveClauseFilters([]);
  }, [activeAlias]);

  // Toggle a clause filter
  const toggleClauseFilter = useCallback((status: ClauseStatus) => {
    setActiveClauseFilters(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  }, []);

  // Navigate to clause detail
  const goToClauseDetail = useCallback((clauseId: number) => {
    setInitialClauseId(clauseId);
    setActivePage('page3');
  }, [setInitialClauseId, setActivePage]);

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage(null);
    setDocumentResult(null);
    setAnalysisProgress(null);
    setClauseSearchQuery('');
    setActiveClauseFilters([]);
    setSelectedHistoryId(null);
    setIsStepperCollapsed(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Handle delete request (opens modal)
  const handleDeleteRequest = useCallback((id: number, name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete({ id, name });
    setDeleteError(null);
    setDeleteModalOpen(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete || isDeleting) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await api.deleteDocument(itemToDelete.id);
      if (response.success) {
        // Remove from list
        setAnalysesList(prev => prev.filter(a => a.document_id !== itemToDelete.id));
        // Clear selection if deleted item was selected
        if (selectedHistoryId === itemToDelete.id) {
          setSelectedHistoryId(null);
          resetState();
        }
        setDeleteModalOpen(false);
        setItemToDelete(null);
      } else {
        setDeleteError(response.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      setDeleteError('Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  }, [itemToDelete, isDeleting, selectedHistoryId, resetState]);

  // Handle delete cancel
  const handleDeleteCancel = useCallback(() => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
    setDeleteError(null);
  }, []);

  // Handle existing analysis - load it
  const handleExistingAnalysisAccept = useCallback(async () => {
    if (!pendingExistingAnalysis) return;
    setExistingAnalysisModalOpen(false);
    await loadHistoryAnalysis(pendingExistingAnalysis.documentId);
    setPendingExistingAnalysis(null);
  }, [pendingExistingAnalysis, loadHistoryAnalysis]);

  // Handle existing analysis - decline (reset)
  const handleExistingAnalysisDecline = useCallback(() => {
    setExistingAnalysisModalOpen(false);
    setPendingExistingAnalysis(null);
    resetState();
  }, [resetState]);

  // Handle new upload from history panel
  const handleNewUploadFromHistory = useCallback(() => {
    resetState();
    setIsHistoryPanelOpen(false);
    fileInputRef.current?.click();
  }, [resetState]);

  const validateFile = (file: File): string | null => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidExtension = ACCEPTED_EXTENSIONS.includes(extension);
    const isValidMime = ACCEPTED_MIME_TYPES.includes(file.type);

    if (!isValidExtension && !isValidMime) {
      return 'Format non supporté. Veuillez choisir un fichier PDF ou DOCX.';
    }

    if (file.size > MAX_FILE_SIZE) {
      return `Le fichier est trop volumineux. Taille maximum : ${MAX_FILE_SIZE / (1024 * 1024)} Mo.`;
    }

    return null;
  };

  const handleFileUpload = useCallback(async (file: File) => {
    // Reset previous state
    setErrorMessage(null);
    setDocumentResult(null);
    setUploadProgress(0);
    setSelectedHistoryId(null);

    // Close history panel when starting upload
    setIsHistoryPanelOpen(false);

    // Create abort controller early to support cancellation at any stage
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Validate file
    const validationError = validateFile(file);
    if (validationError) {
      setErrorMessage(validationError);
      setUploadStatus('error');
      return;
    }

    setSelectedFile(file);
    const contentType = getContentType(file);

    try {
      // Step 1: Hash the file
      setUploadStatus('hashing');
      setUploadProgress(0);
      const fileHash = await computeFileHash(file);

      // Check if cancelled during hashing
      if (signal.aborted) {
        return;
      }

      // Step 2: Init upload - check if file exists
      setUploadStatus('uploading');
      setUploadProgress(10);

      const initResponse = await api.initDocumentUpload({
        filename: file.name,
        file_size: file.size,
        content_type: contentType,
        file_hash: fileHash
      });

      if (!initResponse.success || !initResponse.data) {
        throw new Error(initResponse.error || 'Erreur lors de l\'initialisation de l\'upload');
      }

      const { exists, upload_url, storage_key, document: existingDoc, reactivated_document_id } = initResponse.data;

      // Check if cancelled during initialization
      if (signal.aborted) {
        return;
      }

      // Step 3: Handle based on whether file exists with completed analysis
      if (exists && existingDoc && existingDoc.analysis_id) {
        // Document already analyzed - show confirmation modal
        setUploadStatus('idle');
        setUploadProgress(0);
        setPendingExistingAnalysis({
          documentId: existingDoc.id,
          filename: existingDoc.filename,
          overallNote: existingDoc.overall_note
        });
        setExistingAnalysisModalOpen(true);
        return;
      }

      // Step 4: Upload to S3
      if (!upload_url || !storage_key) {
        throw new Error('URL d\'upload non fournie par le serveur');
      }

      const uploadResult = await api.uploadToS3(
        upload_url,
        file,
        (percent) => {
          // Scale progress: 10-70% for S3 upload
          setUploadProgress(10 + Math.round(percent * 0.6));
        },
        signal,
        contentType
      );

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Erreur lors de l\'upload vers S3');
      }

      // Step 5: Process document (returns immediately)
      setUploadProgress(70);

      // Log if this is a reactivated document case
      if (reactivated_document_id) {
        console.log('[Upload] Reactivated document case - old document ID:', reactivated_document_id);
      }

      const processResponse = await api.processDocument({
        storage_key: storage_key,
        filename: file.name,
        file_hash: fileHash,
        content_type: contentType,
        file_size: file.size,
        reactivated_document_id: reactivated_document_id
      });

      if (!processResponse.success || !processResponse.data) {
        throw new Error(processResponse.error || 'Erreur lors du traitement du document');
      }

      const documentId = processResponse.data.id;
      console.log('[Upload] Process response - new document ID:', documentId, 'status:', processResponse.data.status);

      // Step 6: Subscribe to SSE for analysis progress
      setUploadStatus('analyzing');
      // Initialize with step 1, totalSteps 6 (will be updated by first SSE event)
      setAnalysisProgress({ step: 1, totalSteps: 6, message: 'Démarrage de l\'analyse...' });

      // Create a new abort controller for SSE (the upload one may have been used)
      const sseAbortController = new AbortController();
      abortControllerRef.current = sseAbortController;

      // Track if any SSE events were received
      let receivedAnyEvent = false;
      let fallbackHandled = false;

      // Set up a fallback timeout for reactivated documents
      // If no events are received within 15 seconds, try loading the analysis directly
      if (reactivated_document_id) {
        fallbackTimeoutRef.current = setTimeout(async () => {
          if (!receivedAnyEvent && !fallbackHandled && mountedRef.current) {
            fallbackHandled = true;
            console.log('[SSE Fallback] No events received after 15s for reactivated document, trying direct load...');
            fallbackTimeoutRef.current = null;
            sseAbortController.abort();
            try {
              await loadHistoryAnalysis(documentId);
              console.log('[SSE Fallback] Direct load successful');
            } catch (err) {
              console.error('[SSE Fallback] Direct load failed:', err);
              if (mountedRef.current) {
                setErrorMessage('Erreur lors du chargement de l\'analyse');
                setUploadStatus('error');
              }
            }
          }
        }, 15000);
      }

      await api.subscribeToDocumentProgress(
        documentId,
        (event) => {
          receivedAnyEvent = true;
          if (fallbackTimeoutRef.current) {
            clearTimeout(fallbackTimeoutRef.current);
            fallbackTimeoutRef.current = null;
          }
          // Debug: log received events to understand backend format
          console.log('[SSE Event]', JSON.stringify(event));

          if (event.type === 'started' || event.type === 'progress') {
            const step = event.step ?? 1;
            const totalSteps = event.total_steps ?? 7;
            // Scale progress: 70-100% for analysis
            const progress = 70 + Math.round((step / totalSteps) * 30);
            setUploadProgress(progress);
            setAnalysisProgress(prev => ({
              step,
              totalSteps,
              message: event.message,
              details: event.details ? {
                ...prev?.details,
                ...event.details
              } : prev?.details
            }));
          } else if (event.type === 'clause_result') {
            // Append new clause to the list as it arrives
            // Validate status to ensure it's a known ClauseStatus value
            const validatedStatus: ClauseStatus = isValidClauseStatus(event.status)
              ? event.status
              : 'Incertaine'; // Default fallback for unknown status values

            if (!isValidClauseStatus(event.status)) {
              console.warn(`[SSE] Unknown clause status received: "${event.status}", defaulting to "Incertaine"`);
            }

            const newClause: AnalysisClause = {
              id: event.id,
              document_clause_name: event.document_clause_name,
              matched_clause_name: event.matched_clause_name,
              matched_clause_id: event.matched_clause_id,
              content: event.content,
              status: validatedStatus,
              explanation: event.explanation,
              reformulation: event.reformulation,
            };
            setAnalysisProgress(prev => {
              const existingClauses = prev?.details?.clauses || [];
              // Avoid duplicates: check if clause with same ID already exists
              if (existingClauses.some(c => c.id === newClause.id)) {
                return prev!;
              }
              return {
                step: prev?.step ?? 6,
                totalSteps: prev?.totalSteps ?? 7,
                message: prev?.message ?? 'Audit des clauses...',
                details: {
                  ...prev?.details,
                  clauses: [...existingClauses, newClause]
                }
              };
            });
          } else if (event.type === 'completed') {
            // Clear fallback timeout since we received the completed event
            if (fallbackTimeoutRef.current) {
              clearTimeout(fallbackTimeoutRef.current);
              fallbackTimeoutRef.current = null;
            }

            // Check if we have analysis details from previous events using ref to avoid stale closure
            // For reactivated documents, the backend may skip progress events and send only 'completed'
            // In this case, we need to load the analysis details from the API
            const currentProgress = analysisProgressRef.current;
            const hasDetails = currentProgress?.details?.clauses && currentProgress.details.clauses.length > 0;

            if (!hasDetails && reactivated_document_id) {
              // No details received - this is a reactivated document
              // Load analysis details directly from API
              console.log('[SSE] Completed event received without details for reactivated document, loading from API...');
              if (mountedRef.current) {
                loadHistoryAnalysis(documentId).then(() => {
                  console.log('[SSE] Analysis loaded from API successfully');
                  if (mountedRef.current) {
                    fetchHistoryAnalyses();
                  }
                }).catch(err => {
                  console.error('[SSE] Failed to load analysis from API:', err);
                  if (mountedRef.current) {
                    setErrorMessage('Erreur lors du chargement de l\'analyse');
                    setUploadStatus('error');
                  }
                });
              }
            } else {
              // Normal case: we have details from progress events
              // Run side effects only if mounted
              if (mountedRef.current) {
                setUploadProgress(100);
                setDocumentResult({
                  id: documentId,
                  filename: file.name,
                  status: 'COMPLETED',
                  alreadyExists: false
                });
                setUploadStatus('success');
                setIsStepperCollapsed(true);
                fetchHistoryAnalyses();

                // Update analysis progress state (pure setter callback)
                setAnalysisProgress(prev => ({
                  step: prev?.totalSteps ?? 7,
                  totalSteps: prev?.totalSteps ?? 7,
                  message: 'Analyse terminée',
                  details: {
                    ...prev?.details,
                    // Only update counters if provided in completed event
                    ...(event.details?.expected_clauses_count !== undefined && { expected_clauses_count: event.details.expected_clauses_count }),
                    ...(event.details?.found_clauses_count !== undefined && { found_clauses_count: event.details.found_clauses_count }),
                    ...(event.details?.extra_clauses_count !== undefined && { extra_clauses_count: event.details.extra_clauses_count }),
                    ...(typeof event.details?.final_note === 'number' && { final_note: event.details.final_note }),
                  }
                }));
              }
            }
          } else if (event.type === 'error') {
            // Don't abort here - the SSE will close naturally via reader.cancel() in apiClient
            // Aborting would cause an AbortError that overwrites this error message
            if (mountedRef.current) {
              setErrorMessage(event.message || 'Erreur lors de l\'analyse');
              setUploadStatus('error');
            }
          }
        },
        sseAbortController.signal
      );

    } catch (error: any) {
      // Clear fallback timeout on error
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }

      // Don't show error if fallback already handled the case
      if (error.name === 'AbortError' || error.message === 'Upload cancelled' || error.message === 'SSE connection aborted') {
        // Check if this was triggered by our fallback timeout
        // In that case, don't show an error as the fallback is handling it
        console.log('[Upload] Aborted:', error.message);
      } else if (mountedRef.current) {
        setErrorMessage(error.message || 'Une erreur est survenue');
        setUploadStatus('error');
      }
    }
  }, [fetchHistoryAnalyses, loadHistoryAnalysis]);

  const handleFile = useCallback((file: File) => {
    handleFileUpload(file);
  }, [handleFileUpload]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadStatus === 'idle' || uploadStatus === 'error' || uploadStatus === 'success') {
      setIsDragging(true);
    }
  }, [uploadStatus]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (uploadStatus !== 'idle' && uploadStatus !== 'error' && uploadStatus !== 'success') {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile, uploadStatus]);

  const handleClick = () => {
    if (uploadStatus !== 'idle' && uploadStatus !== 'error' && uploadStatus !== 'success') {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    e.target.value = '';
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    resetState();
  };

  const handleNewUpload = (e: React.MouseEvent) => {
    e.stopPropagation();
    resetState();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  const getStatusMessage = (): string => {
    switch (uploadStatus) {
      case 'hashing':
        return 'Analyse du fichier...';
      case 'uploading':
        return `Upload en cours... ${uploadProgress}%`;
      case 'analyzing':
        if (analysisProgress) {
          return `Étape ${analysisProgress.step}/${analysisProgress.totalSteps} - ${analysisProgress.message}`;
        }
        return 'Analyse en cours...';
      default:
        return '';
    }
  };

  const isProcessing = uploadStatus === 'hashing' || uploadStatus === 'uploading' || uploadStatus === 'analyzing';
  const showProgressBar = isProcessing || uploadStatus === 'success';

  // Get current step number for the progress display
  // Backend sends steps 1-7 directly during analyzing phase
  const getCurrentStep = (): number => {
    if (uploadStatus === 'success') return ANALYSIS_STEPS.length + 1; // All completed
    if (uploadStatus === 'hashing' || uploadStatus === 'uploading') return 1; // Pre-SSE phase
    if (uploadStatus === 'analyzing' && analysisProgress) {
      return analysisProgress.step; // Use backend step directly
    }
    return 0;
  };

  const currentStep = getCurrentStep();

  // Get display message for current step
  const getProgressMessage = (): string => {
    if (uploadStatus === 'success') {
      return 'Votre document est prêt !';
    }
    if (uploadStatus === 'hashing') {
      return 'Préparation de votre document...';
    }
    if (uploadStatus === 'uploading') {
      return 'Envoi du fichier...';
    }
    if (analysisProgress?.message) {
      return analysisProgress.message;
    }
    return 'Traitement en cours...';
  };

  // Show analysis header during processing and after success
  const showAnalysisHeader = isProcessing || uploadStatus === 'success';
  // Show dropzone card only in idle or error state
  const showDropzoneCard = uploadStatus === 'idle' || uploadStatus === 'error';

  // Format date for display with relative dates
  const formatDate = (dateString: string): string => {
    // Backend returns UTC timestamps without 'Z' suffix — ensure UTC parsing
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    const normalized = hasTimezone ? dateString : dateString + 'Z';
    const date = new Date(normalized);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    // Less than 1 minute
    if (diffSeconds < 60) {
      return 'À l\'instant';
    }
    // Less than 1 hour
    if (diffMinutes < 60) {
      return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
    }
    // Less than 24 hours
    if (diffHours < 24) {
      return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
    }
    // Yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Hier';
    }
    // Less than 7 days
    if (diffDays < 7) {
      return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
    }
    // Default: full date
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get score color class
  const getScoreColorClass = (score: number): string => {
    if (score === 5) return 'green';
    if (score >= 3) return 'yellow';
    return 'red';
  };

  return (
    <div className="playbook-page analyse-doc-page">
      <div className="playbook-container">
        {/* History Sidebar - same structure as PlaybookSidebar */}
        <div className={`playbook-sidebar analyse-history-sidebar ${!isHistoryPanelOpen ? 'collapsed' : ''}`}>
          <div className="playbook-sidebar-header">
            <h2>Historique</h2>
            <div className="analyse-header-actions">
              <button
                className="analyse-sidebar-toggle"
                onClick={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)}
                title={isHistoryPanelOpen ? 'Réduire le panneau' : 'Ouvrir l\'historique'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {isHistoryPanelOpen ? (
                    <polyline points="15 18 9 12 15 6" />
                  ) : (
                    <polyline points="9 18 15 12 9 6" />
                  )}
                </svg>
              </button>
              <button
                className="playbook-add-btn"
                onClick={handleNewUploadFromHistory}
                title="Nouvelle analyse"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Analyser</span>
              </button>
            </div>
          </div>

          {/* Admin toggle for personal vs company analyses */}
          {isAdmin && (
            <div className="analyse-scope-toggle">
              <div className="analyse-scope-toggle-inner">
                <div className={`analyse-scope-slider ${showCompanyAnalyses ? 'right' : ''}`} />
                <button
                  className={`analyse-scope-btn ${!showCompanyAnalyses ? 'active' : ''}`}
                  onClick={() => setShowCompanyAnalyses(false)}
                >
                  Mes analyses
                </button>
                <button
                  className={`analyse-scope-btn ${showCompanyAnalyses ? 'active' : ''}`}
                  onClick={() => setShowCompanyAnalyses(true)}
                >
                  Équipe
                </button>
              </div>
            </div>
          )}

          <div className="playbook-list">
            <div className="playbook-list-header">
              <input
                type="text"
                className="playbook-search-input"
                placeholder="Rechercher une analyse"
                value={historySearchQuery}
                onChange={(e) => setHistorySearchQuery(e.target.value)}
              />
              <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </div>

            <div className="playbook-list-items">
              {isLoadingHistory ? (
                <div className="playbook-list-loading">
                  <div className="loading-spinner" />
                </div>
              ) : filteredAnalyses.length > 0 ? (
                filteredAnalyses.map((analysis) => (
                  <div
                    key={analysis.document_id}
                    className={`playbook-list-item analyse-history-item ${selectedHistoryId === analysis.document_id ? 'active' : ''}`}
                    onClick={() => loadHistoryAnalysis(analysis.document_id)}
                  >
                    <div className="analyse-history-item-content">
                      <ScrollingText
                        text={analysis.document_name}
                        className="analyse-history-item-name"
                      />
                      <div className="analyse-history-item-meta">
                        <span className="analyse-history-item-date">{formatDate(analysis.created_at)}</span>
                        <span className={`analyse-history-item-score analyse-history-item-score--${getScoreColorClass(analysis.overall_note)}`}>
                          {analysis.overall_note}/5
                        </span>
                        <div className="analyse-history-item-actions">
                          <button
                            type="button"
                            className="analyse-history-delete-btn"
                            onClick={(e) => handleDeleteRequest(analysis.document_id, analysis.document_name, e)}
                            title="Supprimer"
                            aria-label={`Supprimer ${analysis.document_name}`}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 6h18" />
                              <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            </svg>
                          </button>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chevron-icon">
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : analysesList.length === 0 ? (
                <div className="playbook-list-empty">
                  Aucune analyse dans l'historique
                </div>
              ) : (
                <div className="playbook-list-empty">
                  Aucun résultat pour "{historySearchQuery}"
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`playbook-main-content analyse-main-content ${showProgressBar ? 'processing-mode' : ''}`}>
          {/* Analysis View - shown during processing and after success */}
      {showAnalysisHeader && (
        <div className="analysis-view">
          {/* Document name */}
          <div className="analysis-title-row">
            <div className="analysis-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
            </div>
            <h1 className="analysis-title">{selectedFile?.name || documentResult?.filename}</h1>
          </div>

          {/* Type + Position + Parties row */}
          <div className="analysis-fields-row">
            <div className="analysis-field">
              <span className="analysis-label">Type de document</span>
              {analysisProgress?.details?.contract_type_name ? (
                <span className="analysis-badge">{analysisProgress.details.contract_type_name}</span>
              ) : (
                <span className="analysis-badge analysis-badge--loading"></span>
              )}
            </div>
            <div className="analysis-field">
              <span className="analysis-label">Position de {companyName}</span>
              {analysisProgress?.details?.company_role ? (
                <span className="analysis-chip analysis-chip--role">{analysisProgress.details.company_role}</span>
              ) : (
                <span className="analysis-chip analysis-chip--role analysis-chip--loading"></span>
              )}
            </div>
            <div className="analysis-field analysis-field--grow">
              <span className="analysis-label">Autres parties</span>
              <div className="analysis-chips">
                {analysisProgress?.details?.parties && analysisProgress.details.parties.length > 0 ? (
                  analysisProgress.details.parties
                    .filter(party => party.toLowerCase() !== companyName.toLowerCase())
                    .map((party, index) => (
                      <span key={index} className="analysis-chip">{party}</span>
                    ))
                ) : (
                  <>
                    <span className="analysis-chip analysis-chip--loading"></span>
                    <span className="analysis-chip analysis-chip--loading"></span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="analysis-row analysis-row--block">
            <span className="analysis-label">Résumé</span>
            {analysisProgress?.details?.summary ? (
              <p className="analysis-summary-text">{analysisProgress.details.summary}</p>
            ) : (
              <div className="analysis-summary-lines">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
          </div>

          {/* Analysis Cards */}
          <div className="analysis-cards">
            {/* Card 1: Analyse globale - Non collapsible but has hover */}
            <div className="analysis-card analysis-card--static analysis-card--hoverable">
              <span className="analysis-card-title">Analyse globale</span>
              <div className="analysis-card-content analysis-card-content--centered">
                {uploadStatus === 'success' ? (
                  <span className={`analysis-score analysis-score--${globalScore.colorClass}`}>
                    <span className="analysis-score-value">{globalScore.score}</span>
                    <span className="analysis-score-max">/5</span>
                  </span>
                ) : (
                  <span className="analysis-card-big-value analysis-card-big-value--loading"></span>
                )}
              </div>
            </div>

            {/* Card 2: Respect des directives */}
            <div className="analysis-card analysis-card--static analysis-card--hoverable">
              <span className="analysis-card-title">Respect des directives</span>
              <div className="analysis-card-content analysis-card-content--centered">
                {analysisProgress?.details?.status ? (
                  <span className={`analysis-card-big-value ${analysisProgress.details.status === 'VALID' ? 'analysis-card-big-value--success' : 'analysis-card-big-value--error'}`}>
                    {analysisProgress.details.status === 'VALID' ? 'Validé' : 'Invalidé'}
                  </span>
                ) : (
                  <span className="analysis-card-big-value analysis-card-big-value--loading"></span>
                )}
              </div>
            </div>

            {/* Card 3: Présence des clauses */}
            <div className="analysis-card analysis-card--static analysis-card--hoverable">
              <span className="analysis-card-title">Présence des clauses</span>
              <div className="analysis-card-content">
                {analysisProgress?.details?.expected_clauses_count !== undefined ? (
                  <div className="analysis-card-tags">
                    <span className="analysis-card-tag analysis-card-tag--blue">
                      <strong>{analysisProgress.details.expected_clauses_count}</strong><span>attendues</span>
                    </span>
                    <span className="analysis-card-tag analysis-card-tag--green">
                      <strong>{analysisProgress.details.found_clauses_count || 0}</strong><span>trouvées</span>
                    </span>
                    <span className="analysis-card-tag analysis-card-tag--orange">
                      <strong>{analysisProgress.details.extra_clauses_count || 0}</strong><span>supplémentaires</span>
                    </span>
                  </div>
                ) : (
                  <div className="analysis-card-tags">
                    <span className="analysis-card-tag analysis-card-tag--loading"></span>
                    <span className="analysis-card-tag analysis-card-tag--loading"></span>
                    <span className="analysis-card-tag analysis-card-tag--loading"></span>
                  </div>
                )}
              </div>
            </div>

            {/* Card 4: Conformité des clauses */}
            <div className="analysis-card analysis-card--static analysis-card--hoverable">
              <span className="analysis-card-title">Conformité des clauses</span>
              <div className="analysis-card-content">
                {clausesData.length > 0 ? (
                  <div className="analysis-card-tags">
                    <span className="analysis-card-tag analysis-card-tag--green">
                      <strong>{clausesData.filter(c => c.status === 'Optimale').length}</strong>
                      <span>{clausesData.filter(c => c.status === 'Optimale').length <= 1 ? 'conforme' : 'conformes'}</span>
                    </span>
                    <span className="analysis-card-tag analysis-card-tag--orange">
                      <strong>{clausesData.filter(c => c.status === 'Incertaine').length}</strong>
                      <span>{clausesData.filter(c => c.status === 'Incertaine').length <= 1 ? 'incertaine' : 'incertaines'}</span>
                    </span>
                    <span className="analysis-card-tag analysis-card-tag--red">
                      <strong>{clausesData.filter(c => c.status === 'Critique').length}</strong>
                      <span>{clausesData.filter(c => c.status === 'Critique').length <= 1 ? 'critique' : 'critiques'}</span>
                    </span>
                  </div>
                ) : (
                  <div className="analysis-card-tags">
                    <span className="analysis-card-tag analysis-card-tag--loading"></span>
                    <span className="analysis-card-tag analysis-card-tag--loading"></span>
                    <span className="analysis-card-tag analysis-card-tag--loading"></span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Full-width Directives Box */}
          <div className="analysis-directives-box">
            <div className="analysis-directives-header">
              {analysisProgress?.details?.status && analysisProgress?.details?.contract_type_name ? (
                <span className="analysis-directives-sentence">
                  Le document{' '}
                  <span className={analysisProgress.details.status === 'VALID' ? 'analysis-directives-status--success' : 'analysis-directives-status--error'}>
                    {analysisProgress.details.status === 'VALID' ? 'respecte' : 'ne respecte pas'}
                  </span>
                  {' '}les directives du contrat « {analysisProgress.details.contract_type_name} »
                </span>
              ) : (
                <span className="analysis-directives-sentence analysis-directives-sentence--loading"></span>
              )}
              <button
                className={`analysis-directives-btn ${analysisProgress?.details?.contract_type_id ? '' : 'disabled'}`}
                onClick={() => {
                  if (analysisProgress?.details?.contract_type_id) {
                    setInitialContractId(analysisProgress.details.contract_type_id);
                    setActivePage('page4');
                  }
                }}
                disabled={!analysisProgress?.details?.contract_type_id}
              >
                Voir les directives
              </button>
            </div>
            <div className="analysis-directives-content">
              <div className="analysis-directives-explanation">
                {analysisProgress?.details?.explanation ? (
                  <p>{analysisProgress.details.explanation}</p>
                ) : (
                  <div className="analysis-directives-explanation--loading">
                    <span></span>
                    <span></span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clauses Section */}
          <div className="analysis-clauses-section">
            {/* Header with search and filters */}
            <div className="analysis-clauses-header">
              <div className="analysis-clauses-search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  placeholder="Rechercher une clause..."
                  value={clauseSearchQuery}
                  onChange={(e) => setClauseSearchQuery(e.target.value)}
                />
              </div>
              <div className="analysis-clauses-filters">
                {CLAUSE_FILTERS.map((status) => {
                  const count = clausesData.filter(c => c.status === status).length;
                  return (
                    <button
                      key={status}
                      className={`clause-filter-btn clause-filter-btn--${status} ${activeClauseFilters.includes(status) ? 'active' : ''}`}
                      onClick={() => toggleClauseFilter(status)}
                    >
                      {getStatusLabel(status, count)} ({count})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Clauses list */}
            <div className="analysis-clauses-list">
              {clausesLoading ? (
                // Skeleton placeholders
                <>
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="clause-card clause-card-skeleton">
                      <div className="clause-card-skeleton-header">
                        <span className="clause-card-skeleton-title"></span>
                        <span className="clause-card-skeleton-badge"></span>
                      </div>
                      <div className="clause-card-skeleton-content">
                        <span className="clause-card-skeleton-line"></span>
                        <span className="clause-card-skeleton-line clause-card-skeleton-line--short"></span>
                      </div>
                      <div className="clause-card-skeleton-explanation">
                        <span className="clause-card-skeleton-line"></span>
                      </div>
                    </div>
                  ))}
                </>
              ) : filteredClauses.length > 0 ? (
                filteredClauses.map((clause) => (
                  <div key={clause.id} className={`clause-card clause-card--${clause.status}`}>
                    <div className="clause-card-header">
                      <div className="clause-card-title-row">
                        <span className="clause-card-doc-name">{clause.document_clause_name}</span>
                        <span className={`clause-card-status clause-card-status--${clause.status}`}>
                          {CLAUSE_STATUS_LABELS[clause.status]?.singular || clause.status}
                        </span>
                        {clause.matched_clause_name && clause.status !== 'Supplementaire' && (
                          <span className="clause-card-match-name">→ {clause.matched_clause_name}</span>
                        )}
                      </div>
                      {/* Detail button for matched clauses */}
                      {clause.matched_clause_id && clause.status !== 'Supplementaire' && (
                        <button
                          className="clause-card-detail-btn"
                          onClick={() => goToClauseDetail(clause.matched_clause_id!)}
                        >
                          Voir la clause
                        </button>
                      )}
                    </div>

                    <div className="clause-card-content">
                      <p>{clause.content}</p>
                    </div>

                    <div className="clause-card-explanation">
                      <p>{clause.explanation}</p>
                    </div>

                    {/* Reformulation for Critique/Incertaine */}
                    {(clause.status === 'Critique' || clause.status === 'Incertaine') && clause.reformulation && (
                      <div className="clause-card-reformulation">
                        <div className="clause-card-reformulation-header">
                          <span className="clause-card-reformulation-label">Proposition de reformulation</span>
                          <button
                            className="clause-card-copy-btn"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(clause.reformulation || '');
                              } catch (err) {
                                console.error('Failed to copy to clipboard:', err);
                                // Fallback: try using a textarea for older browsers or insecure contexts
                                try {
                                  const textarea = document.createElement('textarea');
                                  textarea.value = clause.reformulation || '';
                                  textarea.style.position = 'fixed';
                                  textarea.style.opacity = '0';
                                  document.body.appendChild(textarea);
                                  textarea.select();
                                  document.execCommand('copy');
                                  document.body.removeChild(textarea);
                                } catch (fallbackErr) {
                                  console.error('Fallback copy also failed:', fallbackErr);
                                }
                              }
                            }}
                            title="Copier la reformulation"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </button>
                        </div>
                        <p>{clause.reformulation}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="analysis-clauses-empty">
                  <p>Aucune clause ne correspond à vos critères de recherche.</p>
                </div>
              )}
            </div>
          </div>
        </div >
      )}

      {/* Dropzone Card - shown only in idle or error state */}
      {
        showDropzoneCard && (
          <div className="analyse-doc-container">
            <div className="analyse-doc-card">
              <div className="analyse-doc-card-header">
                <div className="analyse-doc-icon-wrapper">
                  <svg
                    className="analyse-doc-header-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <circle cx="11" cy="14" r="3" />
                    <path d="M13.5 16.5L16 19" />
                  </svg>
                </div>
                <div className="analyse-doc-card-title-section">
                  <h1 className="analyse-doc-title">Analyse de document</h1>
                  <p className="analyse-doc-subtitle">
                    Importez un document pour en extraire et analyser le contenu
                  </p>
                </div>
              </div>

              <div className="analyse-doc-card-body">
                <div
                  ref={dropzoneRef}
                  className={`analyse-doc-dropzone ${isDragging ? 'dragging' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                  role="button"
                  tabIndex={0}
                  aria-label="Zone de dépôt de document"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={handleFileInputChange}
                    className="analyse-doc-file-input"
                  />

                  <div className="analyse-doc-dropzone-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <div className="analyse-doc-dropzone-text">
                    <span className="analyse-doc-dropzone-main">
                      Déposer ou <span className="analyse-doc-link">choisir</span> le document à analyser
                    </span>
                  </div>
                </div>

                {/* Error Message */}
                {errorMessage && (
                  <div className="analyse-doc-error">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4M12 16h.01" />
                    </svg>
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="analyse-doc-formats">
                  <span className="analyse-doc-formats-label">Formats acceptés :</span>
                  <div className="analyse-doc-format-tags">
                    <span className="analyse-doc-format-tag">PDF</span>
                    <span className="analyse-doc-format-tag">DOCX</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Fixed bottom stepper - shown during processing and success */}
      {
        showProgressBar && (
          <div className={`stepper-wrapper ${uploadStatus === 'success' ? 'success' : ''} ${isStepperCollapsed ? 'collapsed' : ''}`}>
            {/* Toggle button */}
            <button
              className="stepper-toggle-btn"
              onClick={() => setIsStepperCollapsed(!isStepperCollapsed)}
              title={isStepperCollapsed ? 'Agrandir' : 'Réduire'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isStepperCollapsed ? (
                  <polyline points="18 15 12 9 6 15" />
                ) : (
                  <polyline points="6 9 12 15 18 9" />
                )}
              </svg>
            </button>

            <div className="stepper-container">
              {/* Current step message - hidden when collapsed */}
              {!isStepperCollapsed && (
                <div className={`stepper-message ${uploadStatus === 'success' ? 'success' : ''}`}>
                  {uploadStatus === 'success' ? (
                    <svg className="stepper-message-icon success" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  ) : (
                    <div className="stepper-message-spinner" />
                  )}
                  <span className="stepper-message-text">
                    {getProgressMessage()}
                  </span>
                </div>
              )}

              {/* Step indicators */}
              <div className={`stepper-steps ${isStepperCollapsed ? 'compact' : ''}`}>
                {ANALYSIS_STEPS.map((step, index) => {
                  const isCompleted = currentStep > step.id;
                  const isActive = currentStep === step.id;
                  const isPending = currentStep < step.id;

                  return (
                    <React.Fragment key={step.id}>
                      {/* Step dot */}
                      <div
                        className={`stepper-step ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isPending ? 'pending' : ''}`}
                      >
                        <div className="step-dot">
                          {isCompleted ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          ) : (
                            <span className="step-number">{step.id}</span>
                          )}
                        </div>
                        {!isStepperCollapsed && <span className="step-label">{step.label}</span>}
                      </div>

                      {/* Connecting line (not after last step) */}
                      {index < ANALYSIS_STEPS.length - 1 && (
                        <div className={`stepper-connector ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}>
                          <div className="connector-track" />
                          <div className="connector-fill" />
                          {isActive && <div className="connector-shimmer" />}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Action button for success state - hidden when collapsed */}
              {!isStepperCollapsed && uploadStatus === 'success' && (
                <div className="stepper-actions">
                  <button
                    className="stepper-new-upload-btn"
                    onClick={handleNewUpload}
                  >
                    Analyser un autre document
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Supprimer cette analyse ?"
        message={`Êtes-vous sûr de vouloir supprimer "${itemToDelete?.name}" ? Cette action est irréversible.`}
        isLoading={isDeleting}
        error={deleteError}
      />

      {/* Existing analysis modal */}
      {existingAnalysisModalOpen && pendingExistingAnalysis && (
        <div className="existing-analysis-modal-overlay" onClick={handleExistingAnalysisDecline}>
          <div
            ref={existingAnalysisModalRef}
            className="existing-analysis-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="existing-analysis-modal-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                handleExistingAnalysisDecline();
              }
            }}
          >
            <div className="existing-analysis-modal-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M9 15l2 2 4-4" />
              </svg>
            </div>
            <h3 id="existing-analysis-modal-title" className="existing-analysis-modal-title">Document déjà analysé</h3>
            <p className="existing-analysis-modal-message">
              Ce document a déjà été analysé précédemment.
              {pendingExistingAnalysis.overallNote !== undefined && (
                <span className={`existing-analysis-modal-score existing-analysis-modal-score--${getScoreColorClass(pendingExistingAnalysis.overallNote)}`}>
                  Note : <strong>{pendingExistingAnalysis.overallNote}/5</strong>
                </span>
              )}
            </p>
            <p className="existing-analysis-modal-question">
              Souhaitez-vous consulter l'analyse existante ?
            </p>
            <div className="existing-analysis-modal-actions">
              <button
                type="button"
                className="existing-analysis-modal-btn existing-analysis-modal-btn-cancel"
                onClick={handleExistingAnalysisDecline}
              >
                Non, annuler
              </button>
              <button
                type="button"
                className="existing-analysis-modal-btn existing-analysis-modal-btn-confirm"
                onClick={handleExistingAnalysisAccept}
              >
                Oui, voir l'analyse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
